import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { GenerationSection } from "./GenerateDraftSidebar";
import {
  ModelInfo,
  DocumentType,
  DocumentAnalysisResponse,
  SectionGenerationState,
  InputPanelState,
  UploadedFileInfo,
  getDocumentTypeName,
} from "../types";
import { getSectionConfig } from "../services/api";
import { getFilePreviewUrl, getFilePreviewPdf } from "../services/treatmentPlanSessions";
import ExcelPreview from "./ExcelPreview";
import TreatmentPlanSection from "./TreatmentPlanSection";
import "./GenerateDraftInputPanel.css";

export interface GenerateDraftInputPanelHandle {
  handleGenerate: () => void;
}

interface GenerateDraftInputPanelProps {
  activeSection: GenerationSection;
  models: ModelInfo[];
  onGenerate: (data: {
    section: GenerationSection;
    modelId: string;
    systemPrompt: string;
    userPrompt: string;
    referenceData: Array<{ title: string; content: any }>;
    ragEnabled: boolean;
    ragNumChunks: number;
    ragMinScore: number;
  }) => void;
  sectionState: SectionGenerationState;
  onSectionStateChange: (updates: Partial<SectionGenerationState>) => void;
  analysisResults: Record<DocumentType, DocumentAnalysisResponse | null>;
  generatedSections: Map<GenerationSection, any>;
  inputStateMap: Record<DocumentType, InputPanelState>;
  onSectionDataChange?: (section: GenerationSection, newData: any) => void;
  uploadedFiles?: Record<string, UploadedFileInfo>;
  sessionId?: number | null;
}

const GenerateDraftInputPanel = forwardRef<GenerateDraftInputPanelHandle, GenerateDraftInputPanelProps>(({
  activeSection,
  models,
  onGenerate,
  sectionState,
  onSectionStateChange,
  analysisResults,
  generatedSections,
  inputStateMap,
  onSectionDataChange,
  uploadedFiles,
  sessionId,
}, ref) => {
  const [s3PreviewBlobUrl, setS3PreviewBlobUrl] = useState<string | null>(null);
  const [s3PreviewLoading, setS3PreviewLoading] = useState<boolean>(false);
  const [selectedReferences, setSelectedReferences] = useState<string[]>(
    sectionState.selectedReferences || []
  );

  // Use section state for advanced options and preview
  const showAdvancedOptions = sectionState.showAdvancedOptions || false;
  const previewState = sectionState.previewState || {
    isActive: false,
    title: "",
    data: null,
    originalDocument: null,
    type: "section",
  };

  // Update local state when section state changes
  useEffect(() => {
    setSelectedReferences(sectionState.selectedReferences || []);
  }, [sectionState.selectedReferences]);

  // Get all sections that can be referenced
  const getAllSections = (): GenerationSection[] => {
    return Object.values(GenerationSection);
  };

  // Get available reference data
  const getReferenceOptions = () => {
    const options: {
      value: string;
      label: string;
      data: any;
      isGenerated: boolean;
      type: "analysis" | "section";
      originalDocument?: File | null;
      documentType?: string;
      uploadedFileInfo?: UploadedFileInfo;
    }[] = [];

    // Add analysis results
    Object.entries(analysisResults).forEach(([docType, result]) => {
      const label = getDocumentTypeName(docType);
      const originalFile = inputStateMap[docType as DocumentType]?.file || null;
      const fileInfo = uploadedFiles?.[docType];

      options.push({
        value: `analyzed-${docType}`,
        label: `Analyzed: ${label}`,
        data: result?.data || null,
        isGenerated: result !== null,
        type: "analysis",
        originalDocument: originalFile,
        documentType: docType,
        uploadedFileInfo: fileInfo,
      });
    });

    // Add all sections (both generated and not generated)
    getAllSections().forEach((section) => {
      const data = generatedSections.get(section);
      const label = getSectionName(section);
      options.push({
        value: `section-${section}`,
        label: `Generated: ${label}`,
        data: data || null,
        isGenerated: data !== null && data !== undefined,
        type: "section",
      });
    });

    return options;
  };

  const getSectionName = (section: GenerationSection): string => {
    return section
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  React.useEffect(() => {
    // Load section-specific prompts
    loadSectionPrompts(activeSection);
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSectionPrompts = async (section: GenerationSection) => {
    try {
      // Load section-specific prompts from backend
      const config = await getSectionConfig(section);

      if (!sectionState.hasUnsavedChanges) {
        const updates: Partial<SectionGenerationState> = {
          systemPrompt: config.system_prompt || "",
          userPrompt: config.user_prompt || "",
          selectedModel:
            config.default_model || (models.length > 0 ? models[0].id : ""),
        };

        if (config.default_references && (!sectionState.selectedReferences || sectionState.selectedReferences.length === 0)) {
          updates.selectedReferences = config.default_references;
        }

        onSectionStateChange(updates);
      }
    } catch (error) {
      console.error("Error loading section config from backend:", error);
      if (!sectionState.hasUnsavedChanges) {
        onSectionStateChange({
          systemPrompt: "",
          userPrompt: "",
          selectedModel: models.length > 0 ? models[0].id : "",
        });
      }
    }
  };

  const handleGenerate = () => {
    if (!sectionState.selectedModel) {
      alert("Please select a model");
      return;
    }

    if (selectedReferences.length === 0) {
      alert("Please select at least one reference document");
      return;
    }

    // Get selected reference data as array with title and content
    const options = getReferenceOptions();
    const referenceDataArray: Array<{ title: string; content: any }> = [];

    selectedReferences.forEach((refId) => {
      const option = options.find((opt) => opt.value === refId);
      if (option && option.data) {
        // Use the label without "Analyzed: " or "Generated: " prefix as title
        const title = option.label
          .replace(/^Analyzed:\s*/i, "")
          .replace(/^Generated:\s*/i, "");
        referenceDataArray.push({
          title,
          content: option.data,
        });
      }
    });

    onGenerate({
      section: activeSection,
      modelId: sectionState.selectedModel,
      systemPrompt: sectionState.systemPrompt,
      userPrompt: sectionState.userPrompt,
      referenceData: referenceDataArray,
      ragEnabled: sectionState.ragEnabled || false,
      ragNumChunks: sectionState.ragNumChunks || 5,
      ragMinScore: sectionState.ragMinScore || 0.3,
    });
  };

  useImperativeHandle(ref, () => ({
    handleGenerate,
  }));

  const handlePreview = async (option: any) => {
    // Extract section ID from the value if it's a section preview
    let sectionId: GenerationSection | null = null;
    if (option.type === "section" && option.value.startsWith("section-")) {
      sectionId = option.value.replace("section-", "") as GenerationSection;
    }

    // For analysis previews with no local file but S3 data, fetch from storage
    if (
      option.type === "analysis" &&
      !option.originalDocument &&
      option.uploadedFileInfo &&
      sessionId &&
      option.documentType
    ) {
      // Clean up previous blob URL
      if (s3PreviewBlobUrl) {
        URL.revokeObjectURL(s3PreviewBlobUrl);
        setS3PreviewBlobUrl(null);
      }

      onSectionStateChange({
        previewState: {
          isActive: true,
          title: option.label,
          data: option.data,
          originalDocument: null,
          type: option.type,
          sectionId: null,
        },
        hasUnsavedChanges: true,
      });

      setS3PreviewLoading(true);
      try {
        const isExcel =
          option.uploadedFileInfo.filename?.toLowerCase().endsWith(".xlsx") ||
          option.uploadedFileInfo.filename?.toLowerCase().endsWith(".xls") ||
          option.uploadedFileInfo.mime_type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          option.uploadedFileInfo.mime_type === "application/vnd.ms-excel";

        if (isExcel) {
          const pdfPreview = await getFilePreviewPdf(sessionId, option.documentType);
          if (pdfPreview.pdf_data) {
            const binaryString = atob(pdfPreview.pdf_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: "application/pdf" });
            setS3PreviewBlobUrl(URL.createObjectURL(blob));
          }
        } else {
          const urlResponse = await getFilePreviewUrl(sessionId, option.documentType);
          setS3PreviewBlobUrl(urlResponse.url);
        }
      } catch (error) {
        console.error("Failed to load S3 preview:", error);
      } finally {
        setS3PreviewLoading(false);
      }
      return;
    }

    onSectionStateChange({
      previewState: {
        isActive: true,
        title: option.label,
        data: option.data,
        originalDocument: option.originalDocument || null,
        type: option.type,
        sectionId: sectionId,
      },
      hasUnsavedChanges: true,
    });
  };

  const closePreview = () => {
    if (s3PreviewBlobUrl) {
      URL.revokeObjectURL(s3PreviewBlobUrl);
      setS3PreviewBlobUrl(null);
    }
    onSectionStateChange({
      previewState: {
        ...previewState,
        isActive: false,
      },
      hasUnsavedChanges: true,
    });
  };

  return (
    <div className="generate-input-panel">
      <div className="generate-input-panel__content">
        {previewState.isActive ? (
          <div className="data-preview-overlay">
            <div className="preview-header">
              <label className="preview-title">{previewState.title}</label>
              <div className="preview-header-controls">
                <button className="btn-toggle-expand" onClick={closePreview}>
                  Close Preview
                </button>
              </div>
            </div>
            <div className="preview-content">
              {previewState.type === "analysis" &&
              previewState.originalDocument &&
              previewState.originalDocument.size > 0 ? (
                // Preview for analyzed documents with local file (PDF or Excel)
                previewState.originalDocument.name?.endsWith(".xlsx") ||
                previewState.originalDocument.name?.endsWith(".xls") ||
                previewState.title?.toLowerCase().includes("vb") ||
                previewState.originalDocument.type ===
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                previewState.originalDocument.type ===
                  "application/vnd.ms-excel" ? (
                  <ExcelPreview
                    file={previewState.originalDocument}
                    showPreview={true}
                    autoLoadOnMount={true}
                    containerClassName="no-margin"
                  />
                ) : (
                  <div className="pdf-preview-overlay">
                    <iframe
                      src={URL.createObjectURL(previewState.originalDocument)}
                      title="PDF Preview"
                      className="pdf-preview-iframe"
                      width="100%"
                      height="100%"
                    />
                  </div>
                )
              ) : previewState.type === "analysis" &&
                !previewState.originalDocument ? (
                // Preview for analyzed documents loaded from S3
                s3PreviewLoading ? (
                  <div className="s3-preview-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading preview from storage...</span>
                  </div>
                ) : s3PreviewBlobUrl ? (
                  <div className="pdf-preview-overlay">
                    <iframe
                      src={s3PreviewBlobUrl}
                      title="S3 Document Preview"
                      className="pdf-preview-iframe"
                      width="100%"
                      height="100%"
                    />
                  </div>
                ) : (
                  <div className="s3-preview-unavailable">
                    <span className="placeholder-icon">📄</span>
                    <p>Document preview not available</p>
                    <p className="placeholder-hint">The original document could not be loaded from storage</p>
                  </div>
                )
              ) : previewState.type === "section" && previewState.sectionId ? (
                // Preview for generated sections - use formatted display
                <div className="section-preview-container">
                  <TreatmentPlanSection
                    section={previewState.sectionId as GenerationSection}
                    data={previewState.data}
                    editable={true}
                    onDataChange={(newData) => {
                      // Update the preview state data
                      onSectionStateChange({
                        previewState: {
                          ...previewState,
                          data: newData,
                        },
                      });
                      // Also update the generated sections
                      if (previewState.sectionId && onSectionDataChange) {
                        onSectionDataChange(previewState.sectionId as GenerationSection, newData);
                      }
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : showAdvancedOptions ? (
          <div className="advanced-options-overlay">
            {/* Model Selection */}
            <div className="input-group">
              <div className="input-group__header">
                <label htmlFor="model-select">Select Model</label>
                <button
                  className="btn-reset-field"
                  onClick={async () => {
                    try {
                      const config = await getSectionConfig(activeSection);
                      onSectionStateChange({
                        selectedModel:
                          config.default_model ||
                          (models.length > 0 ? models[0].id : ""),
                        hasUnsavedChanges: true,
                      });
                    } catch (error) {
                      onSectionStateChange({
                        selectedModel: models.length > 0 ? models[0].id : "",
                        hasUnsavedChanges: true,
                      });
                    }
                  }}
                  disabled={sectionState.isGenerating}
                  title="Reset to default model"
                >
                  ↺ Reset
                </button>
              </div>
              <select
                id="model-select"
                value={sectionState.selectedModel || ""}
                onChange={(e) =>
                  onSectionStateChange({
                    selectedModel: e.target.value,
                    hasUnsavedChanges: true,
                  })
                }
                disabled={sectionState.isGenerating}
              >
                <option value="">Choose a model...</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.description || model.provider}
                  </option>
                ))}
              </select>
            </div>

            {/* System Prompt */}
            <div className="input-group">
              <div className="input-group__header">
                <label htmlFor="system-prompt">System Prompt</label>
                <button
                  className="btn-reset-field"
                  onClick={async () => {
                    try {
                      const config = await getSectionConfig(activeSection);
                      onSectionStateChange({
                        systemPrompt: config.system_prompt || "",
                        hasUnsavedChanges: true,
                      });
                    } catch (error) {
                      console.error("Failed to load system prompt from backend:", error);
                      alert("Failed to reset system prompt. Please check backend connection.");
                    }
                  }}
                  disabled={sectionState.isGenerating}
                  title="Reset to default system prompt"
                >
                  ↺ Reset
                </button>
              </div>
              <textarea
                id="system-prompt"
                value={sectionState.systemPrompt || ""}
                onChange={(e) =>
                  onSectionStateChange({
                    systemPrompt: e.target.value,
                    hasUnsavedChanges: true,
                  })
                }
                rows={20}
                disabled={sectionState.isGenerating}
                placeholder="Enter system prompt..."
              />
            </div>

            {/* User Prompt */}
            <div className="input-group">
              <div className="input-group__header">
                <label htmlFor="user-prompt">User Prompt</label>
                <button
                  className="btn-reset-field"
                  onClick={async () => {
                    try {
                      const config = await getSectionConfig(activeSection);
                      onSectionStateChange({
                        userPrompt: config.user_prompt || "",
                        hasUnsavedChanges: true,
                      });
                    } catch (error) {
                      console.error("Failed to load user prompt from backend:", error);
                      alert("Failed to reset user prompt. Please check backend connection.");
                    }
                  }}
                  disabled={sectionState.isGenerating}
                  title="Reset to default user prompt"
                >
                  ↺ Reset
                </button>
              </div>
              <textarea
                id="user-prompt"
                value={sectionState.userPrompt || ""}
                onChange={(e) =>
                  onSectionStateChange({
                    userPrompt: e.target.value,
                    hasUnsavedChanges: true,
                  })
                }
                rows={3}
                disabled={sectionState.isGenerating}
                placeholder="Enter user prompt..."
              />
            </div>

            <div className="input-group rag-config-section">
              <div className="rag-config-header">
                <label className="rag-toggle-label">
                  <input
                    type="checkbox"
                    checked={sectionState.ragEnabled || false}
                    onChange={(e) =>
                      onSectionStateChange({
                        ragEnabled: e.target.checked,
                        hasUnsavedChanges: true,
                      })
                    }
                    disabled={sectionState.isGenerating}
                  />
                  <span>Search in Knowledge Base</span>
                </label>
              </div>

              {sectionState.ragEnabled && (
                <div className="rag-settings">
                  <div className="rag-setting">
                    <label htmlFor="rag-chunks">Chunks to Retrieve</label>
                    <input
                      id="rag-chunks"
                      type="number"
                      min="1"
                      max="20"
                      step="1"
                      value={sectionState.ragNumChunks || 5}
                      onChange={(e) =>
                        onSectionStateChange({
                          ragNumChunks: parseInt(e.target.value) || 5,
                          hasUnsavedChanges: true,
                        })
                      }
                      disabled={sectionState.isGenerating}
                      className="form-control--number"
                    />
                  </div>

                  <div className="rag-setting">
                    <label htmlFor="rag-min-score">
                      Min Relevance Score (%)
                    </label>
                    <input
                      id="rag-min-score"
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={sectionState.ragMinScore || 0.3}
                      onChange={(e) =>
                        onSectionStateChange({
                          ragMinScore: parseFloat(e.target.value) || 0.3,
                          hasUnsavedChanges: true,
                        })
                      }
                      disabled={sectionState.isGenerating}
                      className="form-control--number"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Reference Data Selector */}
            <div className="input-group reference-data-group">
              <div className="reference-header">
                <label htmlFor="reference-select">Reference Data</label>
                <button
                  className="btn-toggle-expand"
                  onClick={() => {
                    setSelectedReferences([]);
                    onSectionStateChange({
                      selectedReferences: [],
                      hasUnsavedChanges: true,
                    });
                  }}
                  disabled={
                    selectedReferences.length === 0 || sectionState.isGenerating
                  }
                  title="Clear all selections"
                >
                  Clear All
                </button>
              </div>
              <div className="reference-selector">
                {getReferenceOptions().map((option) => (
                  <div key={option.value} className="reference-item">
                    <label className="reference-checkbox">
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={selectedReferences.includes(option.value)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newSelection = e.target.checked
                            ? [...selectedReferences, value]
                            : selectedReferences.filter((ref) => ref !== value);
                          setSelectedReferences(newSelection);
                          onSectionStateChange({
                            selectedReferences: newSelection,
                            hasUnsavedChanges: true,
                          });
                        }}
                        disabled={
                          sectionState.isGenerating || !option.isGenerated
                        }
                      />
                      <span className="reference-label">{option.label}</span>
                    </label>
                    {option.isGenerated ? (
                      <button
                        className="btn-preview-reference"
                        onClick={() => handlePreview(option)}
                        title="Preview data"
                      >
                        Preview
                      </button>
                    ) : (
                      <span className="status-not-ready">Not Ready</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

GenerateDraftInputPanel.displayName = "GenerateDraftInputPanel";

export default GenerateDraftInputPanel;
